/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Link,
  Outlet,
  createRootRoute,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import '../App.css';
import React from 'react';
import { useLenis } from 'lenis/react';
import { NavContext } from '../context/NavContext';
import Logo from '../assets/logo-badge.svg?react';
import gsap from 'gsap';

export const Route = createRootRoute({
  component: RootComponent,
});

function NavLinkItem({ to, label, navColor, underlineColor }: { to: string; label: string; navColor: string; underlineColor: string }) {
  const elRef = React.useRef<HTMLAnchorElement>(null);
  const lineRef = React.useRef<HTMLSpanElement>(null);
  const location = useLocation();
  const isActive = location.pathname === to;

  // Sync line with active state when route changes
  React.useEffect(() => {
    const line = lineRef.current;
    if (!line) return;
    gsap.killTweensOf(line);
    gsap.set(line, { scaleX: isActive ? 1 : 0, transformOrigin: 'left center' });
  }, [isActive]);

  React.useEffect(() => {
    const el = elRef.current;
    const line = lineRef.current;
    if (!el || !line) return;

    const onEnter = () => {
      gsap.killTweensOf(line);
      if (isActive) {
        // Line visible — slide it off to the right
        gsap.set(line, { scaleX: 1, transformOrigin: 'right center' });
        gsap.to(line, { scaleX: 0, duration: 0.28, ease: 'power2.out' });
      } else {
        // Line hidden — slide it in from the left
        gsap.set(line, { transformOrigin: 'left center' });
        gsap.to(line, { scaleX: 1, duration: 0.28, ease: 'power2.out' });
      }
    };
    const onLeave = () => {
      gsap.killTweensOf(line);
      if (isActive) {
        // Line was hidden — bring it back from the left
        gsap.set(line, { scaleX: 0, transformOrigin: 'left center' });
        gsap.to(line, { scaleX: 1, duration: 0.22, ease: 'power2.in' });
      } else {
        // Line was visible — slide it off to the right
        gsap.set(line, { scaleX: 1, transformOrigin: 'right center' });
        gsap.to(line, { scaleX: 0, duration: 0.22, ease: 'power2.in' });
      }
    };

    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mouseenter', onEnter);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, [isActive]);

  return (
    <Link
      to={to}
      ref={elRef}
      activeProps={{ className: 'font-bold' }}
      className={`relative inline-block ${navColor} text-l tracking-[4px] font-sans font-light uppercase pointer-events-auto transition-colors duration-700`}
    >
      {label}
      <span
        ref={lineRef}
        aria-hidden
        className="pointer-events-none absolute -bottom-0.5 left-0 h-px w-full"
        style={{ background: underlineColor, transform: isActive ? 'scaleX(1)' : 'scaleX(0)' }}
      />
    </Link>
  );
}

function ScrollReset() {
  const lenis = useLenis();
  const location = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
    lenis?.scrollTo(0, { immediate: true });
  }, [location.pathname, lenis]);

  return null;
}

function useScrollDirection() {
  const [visible, setVisible] = React.useState(true);
  const lastScrollY = React.useRef(0);

  React.useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      const diff = currentY - lastScrollY.current;

      // Only trigger after scrolling a few px to avoid jitter
      if (Math.abs(diff) < 5) return;

      setVisible(currentY < 10 || diff < 0); // show at top or scrolling up
      lastScrollY.current = currentY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return visible;
}

function RootComponent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [, setIsLoggedIn] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const navVisible = useScrollDirection();
  const [hideNav, setHideNav] = React.useState(false);
  const [navTheme, setNavTheme] = React.useState<'light' | 'dark'>('dark');

  React.useEffect(() => {
    const adminPaths = ['/upload', '/projects', '/deploy', '/login'];
    const isAdminRoute = adminPaths.some((p) => location.pathname.startsWith(p));
    if (isAdminRoute) return;

    let timer: ReturnType<typeof setTimeout> | null = null;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.repeat) return;
      timer = setTimeout(() => navigate({ to: '/login', search: { redirect: undefined } }), 2000);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (timer) { clearTimeout(timer); timer = null; }
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      if (timer) clearTimeout(timer);
    };
  }, [location.pathname, navigate]);

  React.useEffect(() => {
    const identity = (window as any).netlifyIdentity;
    const user = identity?.currentUser();
    setIsLoggedIn(!!user);

    const updateAuth = () => {
      setIsLoggedIn(!!(window as any).netlifyIdentity?.currentUser());
    };

    identity?.on('login', updateAuth);
    identity?.on('logout', updateAuth);

    return () => {
      identity?.off('login', updateAuth);
      identity?.off('logout', updateAuth);
    };
  }, []);

  React.useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const lenis = useLenis();

  React.useEffect(() => {
    if (menuOpen) {
      lenis?.stop();
    } else {
      lenis?.start();
    }
  }, [menuOpen, lenis]);

  React.useEffect(() => {
    const handleScroll = () => {
      const elements = document.querySelectorAll('[data-nav-theme]');
      let matched: Element | null = null;
      elements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top <= 0 && rect.bottom > 0) {
          matched = el;
        }
      });
      if (matched) {
        setNavTheme(
          (matched as Element).getAttribute('data-nav-theme') as
            | 'light'
            | 'dark'
        );
      } else {
        setNavTheme('dark');
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [setNavTheme]);

  const scrollToContact = React.useCallback(() => {
    const el = document.getElementById('contact');
    if (el) {
      lenis?.scrollTo(el, { offset: -80 });
    } else {
      navigate({ to: '/' }).then(() => {
        setTimeout(() => {
          const el2 = document.getElementById('contact');
          if (el2) lenis?.scrollTo(el2, { offset: -80 });
        }, 100);
      });
    }
  }, [lenis, navigate]);

  const isUploadPage = location.pathname === '/upload';
  const isProjectsPage = location.pathname.startsWith('/projects');
  const isDeployPage = location.pathname === '/deploy';
  const hideMainHeader = isUploadPage || isProjectsPage || isDeployPage || location.pathname === '/login';

  const navLinks = [
    { to: '/portfolio', label: 'Portfolio' },
    { to: '/testimonials', label: 'Testimonials' },
    { to: '/about', label: 'About' },
  ];

  const navColor = navTheme === 'dark' ? 'text-white' : 'text-neutral-dark';
  const navBorder =
    navTheme === 'dark' ? 'border-white' : 'border-neutral-dark';
  const navFill = navTheme === 'dark' ? 'white' : '#1c1a17';

  return (
    <NavContext.Provider value={{ setHideNav, navTheme, setNavTheme }}>
      {hideMainHeader ? null : (
        <>
          {/* <Link
            to="/"
            className="fixed top-0 left-0 z-50 px-8 py-4 pointer-events-auto"
          >
            <Logo
              className="w-28 h-28 text-[#00391f]"
              style={{ fill: '#00391f' }}
            />
          </Link> */}
          <nav
            className={`fixed top-0 left-0 right-0 z-50 bg-transparent transition-transform duration-300 ease-in-out pointer-events-none ${
              (!hideNav && navVisible) || menuOpen
                ? 'translate-y-0'
                : '-translate-y-[200%]'
            }`}
          >
            <div className="w-full px-8 py-4">
              <div className="flex items-center justify-between">
                {/* Left: nav links */}
                <div className="hidden lg:flex gap-8 text-base lg:flex-1">
                  {navLinks.map(({ to, label }) => (
                    <NavLinkItem key={to} to={to} label={label} navColor={navColor} underlineColor={navFill} />
                  ))}
                </div>

                {/* Center: logo */}
                <div className="lg:flex-1 flex lg:justify-center">
                  <Link
                    to="/"
                    className="w-24 h-24 text-2xl sm:text-3xl font-serif font-bold tracking-widest hover:opacity-80 transition-opacity pointer-events-auto"
                    onClick={() => setMenuOpen(false)}
                  >
                    <Logo
                      className="w-24 h-24"
                      style={{
                        color: navFill,
                        fill: navFill,
                        transition: 'fill 0.7s, color 0.7s',
                      }}
                    />
                  </Link>
                </div>

                {/* Right: Contact Us button (desktop) + hamburger (mobile) */}
                <div className="flex-1 flex justify-end items-center">
                  <button
                    className={`hidden lg:block ${navColor} text-sm tracking-[4px] font-sans font-light uppercase hover:opacity-70 transition-[color,border-color,opacity] duration-700 pointer-events-auto border ${navBorder} px-4 py-2`}
                    onClick={scrollToContact}
                  >
                    Contact Us
                  </button>

                  <button
                    className="lg:hidden flex flex-col justify-center items-center gap-1.5 w-8 h-8 z-60 cursor-pointer pointer-events-auto"
                    onClick={() => setMenuOpen((prev) => !prev)}
                    aria-label="Toggle menu"
                  >
                    <span
                      className={`block h-0.5 w-6 origin-center ${navTheme === 'dark' ? 'bg-white' : 'bg-neutral-dark'}`}
                      style={{
                        transition: 'transform 0.35s ease-in-out, background-color 0.7s',
                        transform: menuOpen ? 'translateY(8px) rotate(45deg)' : 'translateY(0) rotate(0deg)',
                      }}
                    />
                    <span
                      className={`block h-0.5 w-6 origin-center ${navTheme === 'dark' ? 'bg-white' : 'bg-neutral-dark'}`}
                      style={{
                        transition: 'transform 0.15s ease-in, background-color 0.7s',
                        transform: menuOpen ? 'scaleX(0)' : 'scaleX(1)',
                      }}
                    />
                    <span
                      className={`block h-0.5 w-6 origin-center ${navTheme === 'dark' ? 'bg-white' : 'bg-neutral-dark'}`}
                      style={{
                        transition: 'transform 0.35s ease-in-out, background-color 0.7s',
                        transform: menuOpen ? 'translateY(-8px) rotate(-45deg)' : 'translateY(0) rotate(0deg)',
                      }}
                    />
                  </button>
                </div>
              </div>
            </div>
          </nav>

          <div
            className={`lg:hidden fixed inset-0 z-40 flex flex-col items-center justify-center gap-10 backdrop-blur-md bg-black/70 transition-all duration-500 ease-in-out ${
              menuOpen
                ? 'opacity-100 pointer-events-auto'
                : 'opacity-0 pointer-events-none'
            }`}
          >
            {navLinks.map(({ to, label }, i) => (
              <Link
                key={to}
                to={to}
                activeProps={{ className: 'opacity-100 font-bold' }}
                className="text-white text-4xl font-serif tracking-widest hover:opacity-60 transition-all duration-200"
                style={{
                  transitionDelay: menuOpen ? `${i * 60}ms` : '0ms',
                  transform: menuOpen ? 'translateY(0)' : 'translateY(12px)',
                  opacity: menuOpen ? 1 : 0,
                  transition: `opacity 0.4s ease ${i * 60}ms, transform 0.4s ease ${i * 60}ms`,
                }}
              >
                {label}
              </Link>
            ))}
            <button
              className="text-white text-4xl font-serif tracking-widest hover:opacity-60 transition-all duration-200 border border-white px-6 py-3"
              style={{
                transitionDelay: menuOpen ? `${navLinks.length * 60}ms` : '0ms',
                transform: menuOpen ? 'translateY(0)' : 'translateY(12px)',
                opacity: menuOpen ? 1 : 0,
                transition: `opacity 0.4s ease ${navLinks.length * 60}ms, transform 0.4s ease ${navLinks.length * 60}ms`,
              }}
              onClick={() => {
                setMenuOpen(false);
                setTimeout(scrollToContact, 300);
              }}
            >
              Contact Us
            </button>
          </div>
        </>
      )}

      <ScrollReset />
      <main className="flex flex-col flex-1 min-h-screen bg-neutral ">
        <Outlet />
      </main>
    </NavContext.Provider>
  );
}
